import { useState, useCallback } from 'react';
import { getFullUrl } from 'hubspot-url-utils';
import PortalIdParser from 'PortalIdParser';
import { stringify } from 'hub-http/helpers/params';
// @ts-expect-error untyped (see https://git.hubteam.com/HubSpot/ui-addon-iframeable/pull/208)
import UIIFrame from 'ui-addon-iframeable/host/UIIFrame';
// @ts-expect-error untyped (see https://git.hubteam.com/HubSpot/ui-addon-iframeable/pull/208)
import UIModalIFrame from 'ui-addon-iframeable/host/UIModalIFrame';
// @ts-expect-error untyped (see https://git.hubteam.com/HubSpot/ui-addon-iframeable/pull/208)
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
import { useUpgradeEventListener } from 'ui-addon-upgrades/ums/useUpgradeEventListener';
import { useQueryParams } from '../utils/useQueryParams';
import { handleClose } from '../utils/handleClose';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_WIDTH = 600;

/** WARNINGS:

* Any addition/deletion of the props would potentially cause the client and server to be out of sync.
* Use a gate for any props updates and ungate only after making sure synchronization of client and server are confirmed.

*/

const ObjectBuilderClient = ({
  // i.e. { defaultAssociation: { objectId: '123', objectTypeId: '0-1' }, associationTypeId: 456, associationCategory: 'HUBSPOT_DEFINED' }  } }
  appInfo = {},
  // Name of the app embedding the object builder
  appName = 'object-builder-ui',
  // Default property values to prefill into the creator
  defaultProperties,
  // Enable Create tab
  createEnabledOverride,
  // Replaces the tooltip text for disabled create tab
  disabledCreateTabTooltipOverride,
  // Object type ID.
  objectTypeId,
  // Changes the form to edit an existing object, only supported for campaigns
  panelType,
  // Object ID, only used when panelType is not `create`.
  objectId,
  // Callback function called when the modal closes
  onClose,
  // unable to initialize the iframe
  onInitError = () => {},
  // any other message received from iframe (currently none)
  onMessage = () => {},
  // iframe ready
  onReady = () => {},
  // unable to establish communication with the iframe within timeout
  onReadyError = () => {},
  // Whether builder should prefill owner property
  shouldDefaultOwnerId = true,
  // Whether builder should render Create and Add Another button
  shouldRenderCreateAndAddButton = true,
  // Replaces the Create button text
  createButtonMessageOverride = undefined,
  // Whether to show the iframe
  show = true,
  // An initial default pipeline ID used to prefill fields
  initialPipelineId,
  // Enable flexible associations
  enableFlexibleAssociations,
  // a list of object property names that you'd like to appear on the create object form
  extraInitialCreatorPropertyNames,
  // a list of object property names to be hidden from default properties in the creator
  hiddenCreatorPropertyNames,
  // forces the association panel to use single select association
  forceSingleSelectAssociation,
  // a list of object property names that should be added to the association search query
  extraAssociationSearchProperties,
  // Config for an optional alert that displays below the header of the panel
  contextualAlertConfig,
  // Flag for showing embedded message for the object,
  shouldShowEmbeddedMessage,
  // Shows all records, even if already associated (used for quotes)
  showAlreadyAssociatedRecords,
  // Shows multi association options and a search bar for the selected association
  associatorSingleSelectionForMultiOptions = undefined,
  // Whether to render the UI in a condensed style
  condensedUI = false,
  // width of the iframe
  width = DEFAULT_WIDTH,
  // Handles bulk creation of objects
  bulkCreateInfo = undefined,
  // Handles split record creation
  splitRecordInfo = undefined,
  // Flag to use UIIFrame instead of UIModalIFrame
  useUIIFrame = false,
  // Flag to turn off upgrade event listener
  disableUpgradeEventListener = false
}) => {
  const {
    gated,
    ungated
  } = useQueryParams();
  const [iframeReady, setIframeReady] = useState(false);
  const IFrameComponent = useUIIFrame ? UIIFrame : UIModalIFrame;
  if (!disableUpgradeEventListener) {
    // This won't change during the lifetime of the component, so it's safe to call conditionally
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useUpgradeEventListener();
  }
  const handleMessage = useCallback(message => {
    // `UIModalIFrame` automatically handles MODAL_DIALOG_CLOSE messages and forwards them to `onClose(message.payload)`.
    // `UIIFrame` does not. In order to uphold our `onClose` API contract with `useUIIFrame={true}`,
    // we need to reproduce the same behavior
    // See https://git.hubteam.com/HubSpot/ui-addon-iframeable/blob/master/static/js/host/UIModalIFrame.js#L318
    if (useUIIFrame && message.payload.type === MSG_TYPE_MODAL_DIALOG_CLOSE) {
      handleClose(message.payload, onClose);
    } else {
      onMessage(message);
    }
  }, [useUIIFrame, onMessage, onClose]);
  return /*#__PURE__*/_jsx(IFrameComponent, {
    appName: appName,
    appInfo: appInfo,
    height: "100%",
    iframePassthruProps: {
      id: 'object-builder-ui',
      'data-selenium-test': 'associate-panel-iframe',
      'data-test-id': 'object-builder-ui-iframe',
      'data-iframe-ready': iframeReady
    },
    onClose: payload => {
      handleClose(payload, onClose);
    },
    onInitError: onInitError,
    onMessage: handleMessage,
    onReady: (hostCtx, payload) => {
      setIframeReady(true);
      onReady(hostCtx, payload);
    },
    onReadyError: onReadyError,
    show: show,
    embeddedPassthruProps: {
      appName,
      objectId,
      panelType,
      defaultProperties,
      createEnabledOverride,
      disabledCreateTabTooltipOverride,
      initialPipelineId,
      shouldDefaultOwnerId,
      shouldRenderCreateAndAddButton,
      createButtonMessageOverride,
      enableFlexibleAssociations,
      extraInitialCreatorPropertyNames,
      forceSingleSelectAssociation,
      extraAssociationSearchProperties,
      contextualAlertConfig,
      hiddenCreatorPropertyNames,
      shouldShowEmbeddedMessage,
      showAlreadyAssociatedRecords,
      associatorSingleSelectionForMultiOptions,
      bulkCreateInfo,
      splitRecordInfo
    },
    src: `${getFullUrl('app')}/object-builder/${PortalIdParser.get()}/${encodeURIComponent(objectTypeId)}/embed?${stringify({
      gated,
      ungated
    })}${condensedUI ? '&condensedui=true' : ''}`,
    width: width,
    use: "panel"
  });
};
export default ObjectBuilderClient;